import { useCallback } from 'react';

import { useTranslate } from '@/i18n/use-locale';

/**
 * Labels for the enum values the API sends, in the admin's language.
 *
 * The API sends the value (`club`, `awaiting_audit`, `certificate`); the console shows
 * the words under a catalogue prefix — `admin.labels.*` for the shared ones, a screen's
 * own namespace for wording only that screen uses. Given several prefixes it takes the
 * first that knows the value. A value no catalogue knows — one the API adds before the
 * console does — is shown as sent, which beats a blank in a review queue. Same fallback
 * `useCityLabel` uses.
 */
export function useEnumLabel(...prefixes: string[]): (value: string) => string {
  const t = useTranslate();
  // A string, not the array: the callback stays the same across renders.
  const joined = prefixes.join('|');

  return useCallback(
    (value) => {
      for (const prefix of joined.split('|')) {
        const key = `${prefix}.${value}`;
        const label = t(key);
        if (label !== key) return label;
      }
      return value;
    },
    [t, joined],
  );
}

/** Club, federation, academy, agent, sports institution or other. */
export function useOrganizationKindLabel(): (kind: string) => string {
  return useEnumLabel('admin.labels.organizationKind');
}

/** An organization registration's review status, as the verification queue shows it. */
export function useOrgModerationStatusLabel(): (status: string) => string {
  return useEnumLabel('admin.labels.orgModerationStatus');
}

/**
 * A credential document's audit status (ADR-009 pipeline). The audit queue only ever
 * holds `awaiting_audit` items today — the moderation payload carries no per-document
 * status — so the others exist for completeness.
 */
export function useDocumentStatusLabel(): (status: string) => string {
  return useEnumLabel('admin.labels.documentStatus');
}

/**
 * A credential's kind — the "type" line under a document in the audit queue and on the
 * talent detail. The API enumerates `certificate`, `experience`, `course` and
 * `achievement`.
 */
export function useCredentialKindLabel(): (kind: string) => string {
  return useEnumLabel('admin.labels.credentialKind');
}

/** What a document in the audit queue is: a credential attachment or a video introduction. */
export function useModerationDocumentKindLabel(): (kind: string) => string {
  return useEnumLabel('admin.labels.moderationDocumentKind');
}

/**
 * Why a document was rejected: an automated code the pipeline wrote (`duration_exceeded`,
 * `malware_detected`…) in words, or the admin's own reason, which no catalogue knows and
 * is therefore shown as written.
 */
export function useDocumentRejectionLabel(): (reason: string) => string {
  return useEnumLabel('admin.labels.documentRejection');
}

/** A support ticket's status — the inbox tabs and the pill on a thread. */
export function useSupportStatusLabel(): (status: string) => string {
  return useEnumLabel('admin.labels.supportStatus');
}

/** Which side of the platform a requester signs in from: a talent or an organization member. */
export function useAccountKindLabel(): (kind: string) => string {
  return useEnumLabel('admin.labels.accountKind');
}

/** An organization member's role on record — owner, HR manager or member (C4). */
export function useOrgMemberRoleLabel(): (role: string) => string {
  return useEnumLabel('admin.labels.orgMemberRole');
}

/** An organization member's state: invited, active, disabled or revoked. */
export function useOrgMemberStatusLabel(): (status: string) => string {
  return useEnumLabel('admin.labels.orgMemberStatus');
}

/** A club sub-user permission (C4) — the effective set the API sends per member. */
export function useOrgPermissionLabel(): (permission: string) => string {
  const label = useEnumLabel('admin.labels.orgPermission');
  // Keys cannot hold the dots the API uses, so the catalogue keys them with underscores.
  return useCallback(
    (permission) => {
      const resolved = label(permission.replace(/\./g, '__'));
      return resolved === permission.replace(/\./g, '__') ? permission : resolved;
    },
    [label],
  );
}

/** A credential's own lifecycle — awaiting audit, listed, removed (C3). */
export function useCredentialStatusLabel(): (status: string) => string {
  return useEnumLabel('admin.labels.credentialStatus');
}

/** A subscription period's state — active, grace, expired, cancelled. */
export function useSubscriptionStatusLabel(): (status: string) => string {
  return useEnumLabel('admin.labels.subscriptionStatus');
}

/** Which identifier an account signs in with: a phone number or an email. */
export function useIdentityKindLabel(): (kind: string) => string {
  return useEnumLabel('admin.labels.identityKind');
}

/** Whether the account can sign in: active, suspended or deleted. */
export function useAccountStatusLabel(): (status: string) => string {
  return useEnumLabel('admin.labels.accountStatus');
}

export function useGenderLabel(): (gender: string) => string {
  return useEnumLabel('admin.labels.gender');
}

/** Single, married, divorced or widowed — private, shown only in the console. */
export function useMaritalStatusLabel(): (status: string) => string {
  return useEnumLabel('admin.labels.maritalStatus');
}

/** A moderation decision as the history list shows it — approve or reject. */
export function useModerationDecisionLabel(): (decision: string) => string {
  return useEnumLabel('admin.labels.moderationDecision');
}
